// GtPublicShare — the client-facing shop window. Renders OUTSIDE Clerk (no auth).
// Flow: state check -> OTP gate (request, verify, 24h remembered device) -> redacted bundle.
// Every meaningful interaction fires a telemetry beacon; that telemetry is the only
// client-side signal GTCO ever gets, because clients never sign in.
import { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from '../../config';
import brandLogo from '../../brand/main-logo-black.png'; // C1-LOGO

const DEVICE_KEY_PREFIX = 'gtco_share_device_';

function rtwLabel(v) {
  const m = { citizen: 'Citizen', permanent_resident: 'Permanent resident', visa_holder: 'Visa holder', needs_sponsorship: 'Needs sponsorship', unknown: 'Not confirmed' };
  return m[v] || v || 'Not confirmed';
}
function money(n, unit) {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n); if (isNaN(v)) return null;
  const s = '£' + v.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  return unit === 'day' ? s + '/day' : unit === 'hour' ? s + '/hr' : s + '/yr';
}
function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }

function parseSkills(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(s => (typeof s === 'string' ? s : s && (s.name || s.skill))).filter(Boolean);
  if (typeof raw === 'string') { try { const p = JSON.parse(raw); return parseSkills(p); } catch { return raw.split(',').map(s => s.trim()).filter(Boolean); } }
  return [];
}
function dateRange(a, b) {
  const s = a || '', e = b || '';
  if (!s && !e) return '';
  return [s, e].filter(Boolean).join(' – ');
}

// Our structured-interview assessment: the overall result, what was covered,
// a short summary, and each competency with the evidence behind it.
const BAND = { strong: ['Strong match', 'green'], possible: ['Possible match', 'amber'], weak: ['Weaker match', 'slate'], incomplete: ['Partly assessed', 'slate'] };
function fmtScore(n) { return Number.isInteger(n) ? String(n) : Number(n).toFixed(1); }
function GtpsAssessment({ a }) {
  if (!a || !a.criteria || !a.criteria.length) return null;
  const when = a.completedAt ? new Date(a.completedAt) : null;
  const date = when && !isNaN(when.getTime())
    ? String(when.getDate()).padStart(2, '0') + '/' + String(when.getMonth() + 1).padStart(2, '0') + '/' + when.getFullYear() : null;
  const pct = a.overallPct == null ? null : Math.max(0, Math.min(100, Math.round(a.overallPct)));
  const b = BAND[a.band] || null;
  const tone = b ? b[1] : (pct == null ? 'slate' : pct >= 75 ? 'green' : pct >= 55 ? 'amber' : 'slate');
  return (
    <section className="gtps-sec gtps-ax">
      <h3>Our assessment</h3>
      <p className="gtps-ass-sub">{['Structured interview', a.roleTitle ? 'for ' + a.roleTitle : null, date].filter(Boolean).join(' · ')}</p>
      {(pct != null || a.summary || b) && (
        <div className="gtps-ax-hero">
          {pct != null && (
            <div className={'gtps-ax-ring gtps-ax-ring--' + tone} style={{ '--p': pct }} aria-label={pct + '% overall'}>
              <span><b>{pct}%</b><em>overall</em></span>
            </div>
          )}
          <div className="gtps-ax-hero-t">
            <div className="gtps-ax-tags">
              {b && <span className={'gtps-ax-band gtps-ax-band--' + tone}>{b[0]}</span>}
              {a.total ? <span className="gtps-ax-cov">{a.covered} of {a.total} areas covered</span> : null}
            </div>
            {a.summary && <p className="gtps-ax-sum">{a.summary}</p>}
          </div>
        </div>
      )}
      {a.strengths && a.strengths.length > 0 && (
        <ul className="gtps-ax-str">{a.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul>
      )}
      <div className="gtps-ass">
        {a.criteria.map((c, i) => {
          const p = Math.max(0, Math.min(1, c.score / c.max));
          const band = p >= 0.75 ? 'green' : p >= 0.5 ? 'amber' : 'slate';
          return (
            <div key={i} className="gtps-ass-row">
              <div className="gtps-ass-head">
                <span className="gtps-ass-name">{c.criterion}{c.weight === 3 && <em className="gtps-ax-key">Key requirement</em>}</span>
                <span className="gtps-ass-bar"><span className={'gtps-ass-fill gtps-ass-' + band} style={{ width: Math.max(4, p * 100) + '%' }} /></span>
                <span className="gtps-ass-score">{fmtScore(c.score)} / {c.max}</span>
              </div>
              {(c.evidence || []).map((e, j) => <blockquote key={j} className="gtps-ass-quote">“{e}”</blockquote>)}
            </div>
          );
        })}
      </div>
      {a.notCovered && a.notCovered.length > 0 && (
        <p className="gtps-ax-nc">Not covered in the interview: {a.notCovered.join(', ')}.</p>
      )}
      <p className="gtps-ass-note">Scored with the help of artificial intelligence against a fixed set of criteria, by three independent scorers whose results are averaged, and reviewed by a person at Generative Talent Co before sharing.</p>
    </section>
  );
}

// Short clips of their answers, chosen by GTCO, shared with their agreement.
function GtpsClips({ clips, src, onPlay }) {
  const played = useRef({});
  if (!clips || !clips.length) return null;
  const len = (s) => { const v = Math.round(Number(s) || 0); return Math.floor(v / 60) + ':' + String(v % 60).padStart(2, '0'); };
  return (
    <section className="gtps-sec">
      <h3>In their own words</h3>
      <p className="gtps-ass-sub">Short clips from their interview, chosen by Generative Talent Co and shared with their agreement.</p>
      <div className="gtps-clips">
        {clips.map((c) => (
          <figure key={c.id} className="gtps-clip">
            <div className="gtps-clip-v">
              <video src={src(c)} controls preload="metadata" playsInline controlsList="nodownload"
                onPlay={() => { if (!played.current[c.id]) { played.current[c.id] = true; onPlay(c); } }}>
                <track kind="captions" />
              </video>
            </div>
            <figcaption><b>{c.title}</b><span>{len(c.duration_s)}</span></figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

// C1-SHARECHECK: form answers Generative Talent Co chose to share. The server
// has already removed anything that may not go to a client.
function GtpsAnswers({ forms }) {
  if (!forms || !forms.length) return null;
  return (
    <section className="gtps-sec">
      <h3>Their answers</h3>
      <p className="gtps-ass-sub">What they told us directly, shared by Generative Talent Co.</p>
      {forms.map((f) => (
        <div key={f.form_id} className="gtsc-psa">
          <div className="gtsc-psa-h">{f.title}{f.answered_on ? <span> · {f.answered_on}</span> : null}</div>
          <dl className="gtps-dl gtsc-psa-dl">
            {f.items.map((it, i) => <div key={i} className="gtsc-psa-row"><dt>{it.question}</dt><dd>{it.answer}</dd></div>)}
          </dl>
        </div>
      ))}
    </section>
  );
}

// The rest of the dossier: projects, certifications, publications, awards, languages, links.
function GtpsMore({ d }) {
  if (!d) return null;
  const rows = (title, items, render) => (items && items.length ? (
    <section className="gtps-sec">
      <h3>{title}</h3>
      <div className="gtps-edu">{items.map((it, i) => <div key={i} className="gtps-edu-row gtps-more-row">{render(it)}</div>)}</div>
    </section>
  ) : null);
  return (
    <>
      {rows('Projects', d.projects, p => (<>
        <span className="gtps-edu-q">{p.name || 'Project'}</span>
        {p.description && <span className="gtps-more-desc">{p.description}</span>}
        {p.url && <a className="gtps-more-link" href={p.url} target="_blank" rel="noreferrer">{p.url.replace(/^https?:\/\/(www\.)?/, '')}</a>}
      </>))}
      {rows('Certifications', d.certifications, x => (<>
        <span className="gtps-edu-q">{x.name}</span>
        <span className="gtps-edu-i">{[x.issuer, x.year].filter(Boolean).join(' · ')}</span>
      </>))}
      {rows('Publications', d.publications, p => (<>
        <span className="gtps-edu-q">{p.url ? <a className="gtps-more-link" href={p.url} target="_blank" rel="noreferrer">{p.title}</a> : p.title}</span>
        <span className="gtps-edu-i">{[p.publisher, p.year].filter(Boolean).join(' · ')}</span>
      </>))}
      {rows('Awards', d.awards, a => (<>
        <span className="gtps-edu-q">{a.title}</span>
        <span className="gtps-edu-i">{[a.issuer, a.year].filter(Boolean).join(' · ')}</span>
        {a.description && <span className="gtps-more-desc">{a.description}</span>}
      </>))}
      {d.languages && d.languages.length > 0 && (
        <section className="gtps-sec"><h3>Languages</h3>
          <div className="gtps-skills">{d.languages.map((l, i) => <span key={i} className="gtps-skill">{l.language}{l.proficiency ? ' · ' + l.proficiency : ''}</span>)}</div>
        </section>
      )}
      {d.links && d.links.length > 0 && (
        <section className="gtps-sec"><h3>Links</h3>
          <div className="gtps-more-links">{d.links.map((l, i) => (
            <a key={i} className="gtps-more-link" href={/^https?:\/\//i.test(l.value) ? l.value : 'https://' + l.value} target="_blank" rel="noreferrer">
              {l.label || l.kind || l.value.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          ))}</div>
        </section>
      )}
    </>
  );
}

export default function GtPublicShare({ token }) {
  const [phase, setPhase] = useState('loading');
  const [state, setState] = useState(null);
  const [otpHint, setOtpHint] = useState(null);
  const [device, setDevice] = useState(() => { try { return localStorage.getItem(DEVICE_KEY_PREFIX + token) || null; } catch { return null; } });
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [requested, setRequested] = useState(false);
  const [err, setErr] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [verdicts, setVerdicts] = useState({});
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [busyVerdict, setBusyVerdict] = useState(null);

  const seenSections = useRef({});
  const dwellStart = useRef(Date.now());

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(API_BASE + path, { ...opts, headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { const e = new Error(body.error || ('request failed: ' + res.status)); e.status = res.status; throw e; }
    return body;
  }, []);

  const beacon = useCallback((event, extra = {}) => {
    if (!device) return;
    try {
      fetch(API_BASE + '/api/public/shares/' + token + '/event', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, event, ...extra }), keepalive: true,
      }).catch(() => {});
    } catch { /* telemetry must never break the page */ }
  }, [device, token]);

  const loadBundle = useCallback(async (dev) => {
    try {
      const b = await api('/api/public/shares/' + token + '/bundle?device=' + encodeURIComponent(dev));
      setBundle(b); setPhase('bundle');
    } catch (e) {
      if (e.status === 403) setPhase('otp');
      else if (e.status === 410) setPhase('dead');
      else { setErr(e.message); setPhase('error'); }
    }
  }, [api, token]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api('/api/public/shares/' + token + (device ? '?device=' + encodeURIComponent(device) : ''));
        setState(s.state); setOtpHint(s.otp_hint || null);
        if (s.state !== 'active') { setPhase('dead'); return; }
        if (s.verified && device) { loadBundle(device); return; }
        setPhase('otp');
      } catch (e) {
        if (e.status === 404) { setState('missing'); setPhase('dead'); }
        else { setErr(e.message); setPhase('error'); }
      }
    })();
  }, [api, token, device, loadBundle]);

  useEffect(() => { if (phase === 'bundle') beacon('share_opened'); }, [phase, beacon]);

  useEffect(() => {
    if (phase !== 'bundle') return;
    const onLeave = () => beacon('duration_ping', { duration_ms: Date.now() - dwellStart.current });
    window.addEventListener('beforeunload', onLeave);
    return () => { window.removeEventListener('beforeunload', onLeave); onLeave(); };
  }, [phase, beacon]);

  async function requestOtp() {
    setSending(true); setErr(null);
    try {
      const r = await api('/api/public/shares/' + token + '/request-otp', { method: 'POST', body: JSON.stringify({ device_token: device }) });
      if (r.device_token) {
        setDevice(r.device_token);
        try { localStorage.setItem(DEVICE_KEY_PREFIX + token, r.device_token); } catch { /* private mode */ }
      }
      setRequested(true);
    } catch (e) { setErr(e.message); }
    setSending(false);
  }

  async function verifyOtp() {
    if (code.trim().length !== 6) { setErr('Enter the six-digit code.'); return; }
    setVerifying(true); setErr(null);
    try {
      const r = await api('/api/public/shares/' + token + '/verify-otp', { method: 'POST', body: JSON.stringify({ device_token: device, code: code.trim() }) });
      if (r.verified) {
        const dev = r.device_token || device;
        setDevice(dev);
        try { localStorage.setItem(DEVICE_KEY_PREFIX + token, dev); } catch { /* private mode */ }
        await loadBundle(dev);
      } else setErr('That code was not accepted.');
    } catch (e) { setErr(e.message); }
    setVerifying(false);
  }

  function toggleCandidate(c) {
    const next = openId === c.id ? null : c.id;
    setOpenId(next);
    if (next) {
      beacon('candidate_opened', { candidate_id: c.id });
      if (!seenSections.current[c.id]) { seenSections.current[c.id] = true; beacon('section_viewed', { candidate_id: c.id, section: 'profile' }); }
    }
  }

  function downloadPdf(c) {
    // A same-tab link to an attachment downloads the file without leaving the page.
    const base = API_BASE || (window.location.protocol + '//' + window.location.hostname + ':5001');
    const a = document.createElement('a');
    a.href = base + '/api/public/shares/' + token + '/dossier/' + c.id + '/pdf?device=' + encodeURIComponent(device);
    a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  }

  function openResume(c) {
    // window.open is a real navigation, so the CRA dev proxy does not apply.
    // Fall back to the local backend origin when API_BASE is empty (dev).
    const base = API_BASE || (window.location.protocol + '//' + window.location.hostname + ':5001');
    const url = base + '/api/public/shares/' + token + '/cv/' + c.id + '?device=' + encodeURIComponent(device);
    window.open(url, '_blank', 'noopener');
  }

  async function sendVerdict(c, direction, why) {
    setBusyVerdict(c.id);
    try {
      const r = await api('/api/public/shares/' + token + '/verdict', {
        method: 'POST',
        body: JSON.stringify({ device, candidate_id: c.id, direction, reason: why || null }),
      });
      setVerdicts(v => ({ ...v, [c.id]: direction }));
      setDeclineFor(null); setDeclineReason('');
      if (r.revealed) await loadBundle(device);
    } catch (e) { setErr(e.message); }
    setBusyVerdict(null);
  }

  if (phase === 'loading') {
    return <div className="gtps-shell"><div className="gtps-centre"><div className="gtps-spinner" /><p className="gtps-muted">Opening your shortlist…</p></div></div>;
  }

  if (phase === 'dead') {
    const msg = state === 'revoked' ? 'This link has been withdrawn by Generative Talent Co.'
      : state === 'expired' ? 'This link has expired.'
      : 'This link is not valid.';
    return (
      <div className="gtps-shell"><div className="gtps-centre">
        <div className="gtps-card gtps-card-narrow">
          <img src={brandLogo} alt="" className="gtps-logo" />
          <h1 className="gtps-h1">Link unavailable</h1>
          <p className="gtps-muted">{msg}</p>
          <p className="gtps-muted gtps-small">Contact your talent partner for fresh access.</p>
        </div>
      </div></div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="gtps-shell"><div className="gtps-centre">
        <div className="gtps-card gtps-card-narrow">
          <img src={brandLogo} alt="" className="gtps-logo" />
          <h1 className="gtps-h1">Something went wrong</h1>
          <p className="gtps-muted">{err}</p>
        </div>
      </div></div>
    );
  }

  if (phase === 'otp') {
    return (
      <div className="gtps-shell"><div className="gtps-centre">
        <div className="gtps-card gtps-card-narrow">
          <img src={brandLogo} alt="" className="gtps-logo" />
          <h1 className="gtps-h1">Confidential shortlist</h1>
          <p className="gtps-muted">These profiles are private. Verify it is you with a one-time code{otpHint ? <> sent to <b>{otpHint}</b></> : null}.</p>
          {!requested ? (
            <button className="gtps-btn gtps-btn-primary gtps-btn-block" disabled={sending} onClick={requestOtp}>{sending ? 'Sending…' : 'Send me the code'}</button>
          ) : (
            <>
              <label className="gtps-field">
                <span>Six-digit code</span>
                <input className="gtps-otp" inputMode="numeric" maxLength={6} value={code} placeholder="000000"
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => { if (e.key === 'Enter') verifyOtp(); }} autoFocus />
              </label>
              <button className="gtps-btn gtps-btn-primary gtps-btn-block" disabled={verifying} onClick={verifyOtp}>{verifying ? 'Verifying…' : 'View shortlist'}</button>
              <button className="gtps-linkbtn" disabled={sending} onClick={requestOtp}>{sending ? 'Sending…' : 'Send a new code'}</button>
            </>
          )}
          {err && <p className="gtps-err">{err}</p>}
          <p className="gtps-fine">Verified once, this device stays trusted for 24 hours.</p>
        </div>
      </div></div>
    );
  }

  // The client's actions on a person: PDF, CV and their verdict.
  const verdictBlock = (c, anonCard) => (
    <>
                    <div className="gtps-actions">
                      {c.cv_available && (
                        <button className="gtps-btn gtps-btn-ghost" onClick={() => openResume(c)}>Open resume</button>
                      )}
                      {c.pdf_available && (
                        <button className="gtps-btn gtps-btn-ghost" onClick={() => downloadPdf(c)}>Download PDF</button>
                      )}
                      <div className="gtps-verdict">
                        <button
                          className={'gtps-vbtn gtps-vbtn-up' + (verdicts[c.id] === 'up' ? ' gtps-vbtn--on' : '')}
                          disabled={busyVerdict === c.id}
                          onClick={() => sendVerdict(c, 'up')}
                          title="Interested">
                          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12M15 5.88L14 10h5.83a2 2 0 011.92 2.56l-2.33 8A2 2 0 0117.5 22H4a2 2 0 01-2-2v-8a2 2 0 012-2h2.76a2 2 0 001.79-1.11L12 2a3.13 3.13 0 013 3.88z" /></svg>
                          {verdicts[c.id] === 'up' ? 'Interested' : 'Interested'}
                        </button>
                        <button
                          className={'gtps-vbtn gtps-vbtn-down' + (verdicts[c.id] === 'down' ? ' gtps-vbtn--on' : '')}
                          disabled={busyVerdict === c.id}
                          onClick={() => { setDeclineFor(c.id); setDeclineReason(''); }}
                          title="Not for us">
                          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2M9 18.12L10 14H4.17a2 2 0 01-1.92-2.56l2.33-8A2 2 0 016.5 2H20a2 2 0 012 2v8a2 2 0 01-2 2h-2.76a2 2 0 00-1.79 1.11L12 22a3.13 3.13 0 01-3-3.88z" /></svg>
                          Not for us
                        </button>
                      </div>
                    </div>

                    {verdicts[c.id] && declineFor !== c.id && (
                      <p className="gtps-verdict-note">
                        {verdicts[c.id] === 'up'
                          ? (anonCard ? 'Your interest has been sent to your talent partner, who will arrange an introduction. You can change this at any time.' : 'Your interest has been sent to your talent partner. You can change this at any time.')
                          : 'Recorded. You can change your mind at any time.'}
                      </p>
                    )}

                    {declineFor === c.id && (
                      <div className="gtps-decline">
                        <label className="gtps-field">
                          <span>What was missing? One line is enough.</span>
                          <input value={declineReason} placeholder="e.g. Not enough kernel-level depth"
                            onChange={e => setDeclineReason(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && declineReason.trim()) sendVerdict(c, 'down', declineReason); }}
                            autoFocus />
                        </label>
                        <div className="gtps-decline-acts">
                          <button className="gtps-btn gtps-btn-ghost" onClick={() => setDeclineFor(null)}>Cancel</button>
                          <button className="gtps-btn gtps-btn-primary" disabled={!declineReason.trim() || busyVerdict === c.id}
                            onClick={() => sendVerdict(c, 'down', declineReason)}>
                            {busyVerdict === c.id ? 'Sending…' : 'Send feedback'}
                          </button>
                        </div>
                      </div>
                    )}
    </>
  );

  const cands = (bundle && bundle.candidates) || [];
  const vis = (bundle && bundle.visibility) || {};

  return (
    <div className="gtps-shell gtps-shell-bundle">
      <header className="gtps-header">
        <div className="gtps-header-in">
          <img src={brandLogo} alt="" className="gtps-logo gtps-logo-sm" />
          <span className="gtps-conf">Confidential</span>
        </div>
      </header>

      <main className="gtps-main">
        <div className="gtps-intro">
          <p className="gtps-eyebrow">Prepared for you</p>
          <h1 className="gtps-title">{bundle.title || 'Your shortlist'}</h1>
          <p className="gtps-sub">{cands.length} candidate{cands.length === 1 ? '' : 's'}, sourced and vetted by Generative Talent Co.{bundle.mode === 'anonymous' ? ' Presented anonymously: open a card to see what they have done.' : ' Open a card for the full career history.'}</p>
        </div>

        <div className="gtps-list">
          {cands.map((c, idx) => {
            // C2-ANON: an anonymous card. Built only from what the server sent.
            if (c.anonymous_card) {
              const k = c.anonymous_card;
              const open = openId === c.id;
              return (
                <article key={c.id} className={'gtps-cand gtps-cand--anon' + (open ? ' gtps-cand--open' : '')}>
                  <button type="button" className="gtps-cand-head" onClick={() => toggleCandidate(c)}>
                    <span className="gtps-num">{String(idx + 1).padStart(2, '0')}</span>
                    <span className="gtps-av gtps-av-anon">◆</span>
                    <span className="gtps-cand-id">
                      <span className="gtps-cand-name">{k.headline || 'Candidate ' + c.anon_ref}</span>
                      <span className="gtps-cand-role">{[k.seniority, k.company_type].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="gtps-cand-right">
                      <span className="gtps-cand-meta">
                        {k.location && <span className="gtps-chip">{k.location}</span>}
                        {k.salary_band && <span className="gtps-chip gtps-chip-rate">{k.salary_band}</span>}
                        {k.availability_confirmed && <span className="gtps-chip">{k.availability_confirmed}</span>}{/* C2-AVAILCONF */}
                      </span>
                      <span className="gtps-open-hint">{open ? 'Close' : 'View profile'}</span>
                    </span>
                  </button>
                  {open && (
                    <div className="gtps-cand-body">
                      {k.highlights && k.highlights.length > 0 && (
                        <section className="gtps-sec"><h3>What they have done</h3>
                          <ul className="gtps-anon-hl">{k.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
                        </section>
                      )}
                      <GtpsAssessment a={k.assessment} />
                      {k.career && k.career.length > 0 && (
                        <section className="gtps-sec"><h3>Career</h3>
                          <ol className="gtps-timeline">
                            {k.career.map((r, i) => (
                              <li key={i} className="gtps-tl-item">
                                <span className="gtps-tl-dot" />
                                <div className="gtps-tl-body">
                                  <div className="gtps-tl-head"><span className="gtps-tl-title">{r.role || 'Role'}</span>{r.company_type && <span className="gtps-tl-co">{r.company_type}</span>}</div>
                                  {r.years && <div className="gtps-tl-dates">{r.years}</div>}
                                </div>
                              </li>
                            ))}
                          </ol>
                        </section>
                      )}
                      {k.skills && k.skills.length > 0 && (
                        <section className="gtps-sec"><h3>Skills</h3>
                          <div className="gtps-skills">{k.skills.map((x, i) => <span key={i} className="gtps-skill">{x}</span>)}</div>
                        </section>
                      )}
                      <section className="gtps-sec"><h3>At a glance</h3>
                        <dl className="gtps-dl">
                          {k.seniority && <><dt>Seniority</dt><dd>{k.seniority}</dd></>}
                          {k.years_experience && <><dt>Experience</dt><dd>About {k.years_experience} years</dd></>}
                          {k.company_type && <><dt>Works at</dt><dd>{k.company_type}</dd></>}
                          {k.location && <><dt>Location</dt><dd>{k.location}</dd></>}
                          {k.salary_band && <><dt>Salary band</dt><dd>{k.salary_band}</dd></>}
                          {(k.notice || k.availability_confirmed) && <><dt>Availability</dt><dd>{[k.notice, k.availability_confirmed].filter(Boolean).join(' \u00b7 ')}</dd></>}
                          {k.education && <><dt>Education</dt><dd>{k.education}</dd></>}
                          <><dt>Reference</dt><dd className="gtps-ref">{c.anon_ref}</dd></>
                        </dl>
                      </section>
                      <p className="gtps-anon-note">Presented anonymously. Tell us you are interested and your talent partner will arrange an introduction, with the candidate’s agreement.</p>
                      {verdictBlock(c, true)}
                    </div>
                  )}
                </article>
              );
            }
            const name = c.full_name || c.display_name || c.anon_ref;
            const anon = !c.full_name;
            const rcv = c.redacted_cv || null;
            const skillsTop = parseSkills(c.skills);
            const skills = skillsTop.length ? skillsTop : (rcv ? (rcv.skills || []) : []);
            const exp = rcv ? (rcv.experience || []) : [];
            const edu = rcv ? (rcv.education || []) : [];
            const open = openId === c.id;
            const rate = money(c.expected_rate_gbp, c.expected_rate_unit);
            const current = exp.length ? exp[0] : null;

            return (
              <article key={c.id} className={'gtps-cand' + (open ? ' gtps-cand--open' : '')}>
                <button type="button" className="gtps-cand-head" onClick={() => toggleCandidate(c)}>
                  <span className="gtps-num">{String(idx + 1).padStart(2, '0')}</span>
                  <span className={'gtps-av' + (anon ? ' gtps-av-anon' : '')} style={anon ? undefined : { background: avatarColour(name) }}>{anon ? '◆' : initials(name)}</span>
                  <span className="gtps-cand-id">
                    <span className="gtps-cand-name">{name}</span>
                    {current && <span className="gtps-cand-role">{[current.title, current.company].filter(Boolean).join(' · ')}</span>}
                    {c.headline && <span className="gtps-cand-headline">{c.headline}</span>}
                  </span>
                  <span className="gtps-cand-right">
                    <span className="gtps-cand-meta">
                      {c.location && <span className="gtps-chip">{c.location}</span>}
                      {vis.right_to_work && c.right_to_work_status && <span className="gtps-chip">{rtwLabel(c.right_to_work_status)}</span>}
                      {rate && <span className="gtps-chip gtps-chip-rate">{rate}</span>}
                    </span>
                    <span className="gtps-open-hint">{open ? 'Close' : 'View profile'}</span>
                  </span>
                </button>

                {open && (
                  <div className="gtps-cand-body">
                    {c.summary && <section className="gtps-sec"><h3>Profile</h3><p className="gtps-prose">{c.summary}</p></section>}

                    <GtpsAssessment a={c.dossier && c.dossier.assessment} />

                    <GtpsClips clips={c.dossier && c.dossier.clips}
                      src={(clip) => (API_BASE || (window.location.protocol + '//' + window.location.hostname + ':5001')) + '/api/public/shares/' + token + '/clips/' + clip.id + '?device=' + encodeURIComponent(device)}
                      onPlay={() => beacon('section_viewed', { candidate_id: c.id, section: 'interview_clip' })} />

                    <GtpsAnswers forms={c.form_answers} />

                    {exp.length > 0 && (
                      <section className="gtps-sec">
                        <h3>Career history</h3>
                        <ol className="gtps-timeline">
                          {exp.map((e, i) => (
                            <li key={i} className="gtps-tl-item">
                              <span className="gtps-tl-dot" />
                              <div className="gtps-tl-body">
                                <div className="gtps-tl-head">
                                  <span className="gtps-tl-title">{e.title || 'Role'}</span>
                                  {e.company && <span className="gtps-tl-co">{e.company}</span>}
                                </div>
                                {dateRange(e.start, e.end) && <div className="gtps-tl-dates">{dateRange(e.start, e.end)}</div>}
                                {e.summary && <p className="gtps-tl-sum">{e.summary}</p>}
                              </div>
                            </li>
                          ))}
                        </ol>
                      </section>
                    )}

                    {edu.length > 0 && (
                      <section className="gtps-sec">
                        <h3>Education</h3>
                        <div className="gtps-edu">
                          {edu.map((e, i) => (
                            <div key={i} className="gtps-edu-row">
                              <span className="gtps-edu-q">{e.qualification || 'Qualification'}</span>
                              <span className="gtps-edu-i">{e.institution || ''}{dateRange(e.start, e.end) ? ' · ' + dateRange(e.start, e.end) : ''}</span>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    {skills.length > 0 && (
                      <section className="gtps-sec"><h3>Skills</h3>
                        <div className="gtps-skills">{skills.map((s, i) => <span key={i} className="gtps-skill">{s}</span>)}</div>
                      </section>
                    )}

                    <GtpsMore d={c.dossier} />

                    <section className="gtps-sec"><h3>At a glance</h3>
                      <dl className="gtps-dl">
                        {c.location && <><dt>Location</dt><dd>{c.location}</dd></>}
                        {vis.right_to_work && <><dt>Right to work</dt><dd>{rtwLabel(c.right_to_work_status)}</dd></>}
                        {rate && <><dt>Rate expectation</dt><dd>{rate}</dd></>}
                        {c.email && <><dt>Email</dt><dd><a href={'mailto:' + c.email}>{c.email}</a></dd></>}
                        {c.phone && <><dt>Phone</dt><dd>{c.phone}</dd></>}
                        {c.linkedin_url && <><dt>LinkedIn</dt><dd><a href={c.linkedin_url} target="_blank" rel="noreferrer">View profile</a></dd></>}
                        <><dt>Reference</dt><dd className="gtps-ref">{c.anon_ref}</dd></>
                      </dl>
                    </section>

                    {anon && <p className="gtps-anon-note">Presented anonymously. Their identity is released the moment you register interest.</p>}

                    {verdictBlock(c, false)}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <footer className="gtps-foot">
          <div className="gtps-foot-cta">
            <h2>Interested in any of these candidates?</h2>
            <p>Reply to the email that brought you here and your talent partner will arrange the next step.</p>
          </div>
          <p className="gtps-fine">Shared in confidence by Generative Talent Co. Please do not forward this link.</p>
        </footer>
      </main>
    </div>
  );
}
